import { BrowserProvider, Contract, JsonRpcProvider, parseUnits } from 'ethers';
import { pickActiveEvmWallet, connectEvmWallet } from './detectEvmWallets';
import { EVM_WALLET_SIGN_MSG, evmWalletMismatchMsg } from './evmWalletMessages';

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
];

export async function fetchBridgeConfig(apiBase) {
  const axios = (await import('axios')).default;
  const { data } = await axios.get(`${apiBase}/hyperliquid/bridge-config`);
  return data;
}

function isUserRejection(err) {
  const raw = String(err?.message || err || '').toLowerCase();
  return err?.code === 4001 || /user rejected|denied|cancelled|canceled/i.test(raw);
}

function isRpcFetchFailure(err) {
  const raw = String(err?.message || err?.shortMessage || err || '').toLowerCase();
  return (
    raw.includes('failed to fetch') ||
    raw.includes('network error') ||
    raw.includes('could not coalesce error') ||
    raw.includes('eth_blocknumber')
  );
}

/** User-facing message for bridge / MetaMask RPC failures. */
export function formatBridgeDepositError(err, bridgeConfig) {
  if (isUserRejection(err)) {
    return 'Deposit cancelled in wallet.';
  }
  const chain = bridgeConfig?.chainName || 'Arbitrum One';
  if (isRpcFetchFailure(err)) {
    return (
      `Could not reach ${chain} through MetaMask (RPC "Failed to fetch"). ` +
      `Open MetaMask → switch network to **${chain}** (not Ethereum mainnet or "Hyperliquid signing"), unlock the wallet, then retry. ` +
      'If it persists: disable VPN/ad-blockers, refresh the page, or try Rabby with the same 0x address.'
    );
  }
  if (err?.code === 4902 || /unrecognized chain/i.test(String(err?.message || ''))) {
    return `Add ${chain} in MetaMask when prompted, then retry the deposit.`;
  }
  return err?.message || String(err);
}

export async function ensureArbitrumNetwork(provider, bridgeConfig) {
  const chainIdHex = String(bridgeConfig.chainIdHex || '').toLowerCase();
  const chainName = bridgeConfig.chainName || 'Arbitrum One';

  let current = await provider.request({ method: 'eth_chainId' }).catch(() => null);
  if (current && String(current).toLowerCase() === chainIdHex) return;

  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: bridgeConfig.chainIdHex }],
    });
  } catch (e) {
    if (isUserRejection(e)) throw e;
    if (e?.code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: bridgeConfig.chainIdHex,
            chainName: bridgeConfig.chainName,
            nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
            rpcUrls: [bridgeConfig.rpcUrl],
            blockExplorerUrls: [bridgeConfig.explorerUrl],
          },
        ],
      });
    } else {
      throw new Error(
        `Could not switch MetaMask to ${chainName}. Open MetaMask, select ${chainName} manually, and retry.`
      );
    }
  }

  current = await provider.request({ method: 'eth_chainId' }).catch(() => null);
  if (current && String(current).toLowerCase() !== chainIdHex) {
    throw new Error(
      `MetaMask is on the wrong network. Select **${chainName}** in MetaMask (not "Hyperliquid signing" / Sepolia), then retry.`
    );
  }
}

/** Read Arbitrum USDC balance via public RPC — no wallet popup. */
export async function getArbitrumUsdcBalanceReadOnly(ownerAddress, bridgeConfig) {
  if (!ownerAddress || !bridgeConfig?.rpcUrl) return null;
  const provider = new JsonRpcProvider(bridgeConfig.rpcUrl, bridgeConfig.chainId);
  const contract = new Contract(bridgeConfig.usdc, ERC20_ABI, provider);
  const bal = await contract.balanceOf(ownerAddress);
  return Number(bal) / 1e6;
}

/** Read native ETH on Arbitrum (for gas) — no wallet popup. */
export async function getArbitrumEthBalanceReadOnly(ownerAddress, bridgeConfig) {
  if (!ownerAddress || !bridgeConfig?.rpcUrl) return null;
  const provider = new JsonRpcProvider(bridgeConfig.rpcUrl, bridgeConfig.chainId);
  const bal = await provider.getBalance(ownerAddress);
  return Number(bal) / 1e18;
}

/** Block deposit before opening MetaMask when balance or gas is obviously insufficient. */
export async function validateBridgeDepositPreflight({ walletAddress, amountUsdc, bridgeConfig }) {
  const min = bridgeConfig?.minDepositUsdc || 5;
  const amount = Number(amountUsdc);
  if (!Number.isFinite(amount) || amount < min) {
    throw new Error(`Minimum deposit is ${min} USDC`);
  }

  const [usdc, eth] = await Promise.all([
    getArbitrumUsdcBalanceReadOnly(walletAddress, bridgeConfig),
    getArbitrumEthBalanceReadOnly(walletAddress, bridgeConfig),
  ]);

  if (usdc != null && amount > usdc + 0.000001) {
    throw new Error(
      `Only ${usdc.toFixed(2)} USDC on ${bridgeConfig.chainName || 'Arbitrum'}. ` +
        `Add at least ${(amount - usdc).toFixed(2)} more USDC to that wallet, or lower the deposit amount.`
    );
  }

  if (eth != null && eth < 0.00005) {
    throw new Error(
      `No ETH on ${bridgeConfig.chainName || 'Arbitrum'} for gas. ` +
        'Send a small amount of ETH to the same wallet on Arbitrum (often ~$0.50–$2), then retry.'
    );
  }

  return { usdc, eth };
}

export async function getArbitrumUsdcBalance(provider, ownerAddress, bridgeConfig) {
  await ensureArbitrumNetwork(provider, bridgeConfig);
  const browserProvider = new BrowserProvider(provider);
  const contract = new Contract(bridgeConfig.usdc, ERC20_ABI, browserProvider);
  const bal = await contract.balanceOf(ownerAddress);
  return Number(bal) / 1e6;
}

export async function depositUsdcToHyperliquid(provider, fromAddress, amountUsdc, bridgeConfig) {
  const min = bridgeConfig.minDepositUsdc || 5;
  const amount = Number(amountUsdc);
  if (!Number.isFinite(amount) || amount < min) {
    throw new Error(`Minimum deposit is ${min} USDC`);
  }

  try {
    await ensureArbitrumNetwork(provider, bridgeConfig);
  } catch (e) {
    throw new Error(formatBridgeDepositError(e, bridgeConfig));
  }

  let browserProvider;
  try {
    browserProvider = new BrowserProvider(provider);
    await browserProvider.getNetwork();
  } catch (e) {
    throw new Error(formatBridgeDepositError(e, bridgeConfig));
  }

  const signer = await browserProvider.getSigner(fromAddress);
  const contract = new Contract(bridgeConfig.usdc, ERC20_ABI, signer);

  let receipt;
  try {
    const units = parseUnits(amount.toFixed(6), 6);
    const tx = await contract.transfer(bridgeConfig.bridge, units);
    receipt = await tx.wait();
  } catch (e) {
    throw new Error(formatBridgeDepositError(e, bridgeConfig));
  }
  return {
    txHash: receipt.hash,
    explorerUrl: `${bridgeConfig.explorerUrl}/tx/${receipt.hash}`,
  };
}

export async function runBridgeDeposit({ walletAddress, amountUsdc, bridgeConfig }) {
  const w = await pickActiveEvmWallet();
  if (!w) throw new Error(EVM_WALLET_SIGN_MSG);

  const { address } = await connectEvmWallet(w.provider);
  if (
    walletAddress &&
    address.toLowerCase() !== String(walletAddress).trim().toLowerCase()
  ) {
    throw new Error(evmWalletMismatchMsg(address));
  }

  return depositUsdcToHyperliquid(w.provider, address, amountUsdc, bridgeConfig);
}
